// Shared sentiment / conflict analysis for Slack + Teams anomaly detection.
//
// All detection is purely client-side keyword heuristics — no external API calls.

import type { AnomalyRelatedMessage, MessagingAnomaly, MessagingMessage, MessagingPlatform } from './messaging-types';
import { toEmoji } from '../lib/emoji';

// ─── Keyword list ─────────────────────────────────────────────────────────────

/** Words and phrases that signal conflict, frustration or disagreement. */
export const CONFLICT_WORDS: string[] = [
  // Direct disagreement
  'disagree', "you're wrong", 'you are wrong', "that's wrong", "that is wrong",
  "that's not right", "that is not right", 'incorrect', 'misleading', 'actually no',
  "no that's", 'no thats', 'you misunderstood', 'that makes no sense',
  // Frustration / anger
  'frustrated', 'frustrating', 'ridiculous', 'unacceptable', 'disappointing',
  'terrible', 'awful', 'pathetic', 'absurd', 'outrageous', 'infuriating',
  'this is a joke', 'incompetent', 'useless',
  // Dismissive / escalating
  'as i said', 'like i said', 'i already said', 'i told you', 'i said this',
  'seriously?', 'seriously??', 'seriously???', 'come on', 'are you kidding',
  'are you serious', 'give me a break', "you're not listening", 'stop it',
  'enough already', 'stop ignoring', 'why are you',
  // Profanity / strong language
  'wtf', 'what the hell', 'ffs', 'for f', 'bullshit', ' bs ', 'what is wrong with',
  "can't believe this", 'unbelievable', 'fuck', 'shit', 'bitch', 'bastard',
  'asshole', 'idiot', 'moron', 'screw you', 'piss off', 'go to hell',
];

/** Returns the number of conflict keywords present in a message (case-insensitive). */
export function conflictScore(text: string): number {
  const lower = text.toLowerCase();
  return CONFLICT_WORDS.filter(w => lower.includes(w)).length;
}

// ─── Heated thread detection ──────────────────────────────────────────────────

export interface HeatedThread {
  threadId: string;
  channelId: string;
  channelName: string;
  authorNames: string[];
  conflictMessageCount: number;
  replyCount: number;
  avgGapSeconds: number;
}

/**
 * Identify threads that are "heated": ≥4 replies between ≥2 authors,
 * ≥2 messages contain conflict language, and avg gap < 5 min per reply.
 */
export function detectHeatedThreads(
  messages: MessagingMessage[],
  channelNameMap: Map<string, string>,
): HeatedThread[] {
  const threads = new Map<string, MessagingMessage[]>();
  for (const msg of messages) {
    if (msg.isReply && msg.threadId) {
      if (!threads.has(msg.threadId)) threads.set(msg.threadId, []);
      threads.get(msg.threadId)!.push(msg);
    }
  }

  const results: HeatedThread[] = [];
  for (const [threadId, replies] of threads) {
    if (replies.length < 4) continue;

    const authorIds = new Set(replies.map(m => m.authorId));
    if (authorIds.size < 2) continue;

    const conflictCount = replies.filter(m => conflictScore(m.text) > 0).length;
    if (conflictCount < 2) continue;

    const times = replies
      .map(m => new Date(m.timestamp).getTime())
      .sort((a, b) => a - b);
    const totalDurationMs = times[times.length - 1] - times[0];
    const avgGapMs = totalDurationMs / Math.max(replies.length - 1, 1);

    if (avgGapMs < 5 * 60 * 1000) {
      const channelId = replies[0].channelId;
      results.push({
        threadId,
        channelId,
        channelName: channelNameMap.get(channelId) ?? channelId,
        authorNames: [...new Set(replies.map(m => m.authorName))],
        conflictMessageCount: conflictCount,
        replyCount: replies.length,
        avgGapSeconds: Math.round(avgGapMs / 1000),
      });
    }
  }
  return results;
}

// ─── Negative reaction emojis ────────────────────────────────────────────────

/** Emoji names (Slack format) that signal negative sentiment or disapproval. */
export const NEGATIVE_EMOJIS = new Set([
  // Thumbs down
  'thumbsdown', '-1', 'thumbs_down',
  // Anger / frustration
  'rage', 'angry', 'anger', 'unamused', 'face_with_symbols_on_mouth',
  // Disappointment
  'disappointed', 'disappointed_relieved', 'cry', 'sob', 'pensive',
  // Facepalm
  'man-facepalming', 'woman-facepalming', 'facepalm', 'face_palm',
  // No / stop / prohibited
  'no_entry', 'no_entry_sign', 'x', 'negative_squared_cross_mark', 'prohibited',
  // Skull
  'skull', 'skull_and_crossbones',
  // Other dismissive
  'broken_heart', 'face_with_raised_eyebrow', 'raised_eyebrow', 'eye_roll',
]);

/** Strip Slack skin-tone modifiers (e.g. "-1::skin-tone-3" → "-1") before checking the set. */
export function isNegativeEmoji(name: string): boolean {
  const base = name.split('::')[0]; // "-1::skin-tone-3" → "-1"
  return NEGATIVE_EMOJIS.has(base) || NEGATIVE_EMOJIS.has(name);
}

// ─── Main sentiment anomaly detector ─────────────────────────────────────────

/**
 * Analyse messages for conflict / sentiment anomalies.
 * Returns anomaly objects ready to be merged with the structural anomaly list.
 */
export function detectSentimentAnomalies(
  messages: MessagingMessage[],
  platform: MessagingPlatform,
  channelStats: { channelId: string; channelName: string }[],
): MessagingAnomaly[] {
  const anomalies: MessagingAnomaly[] = [];
  const now = new Date().toISOString();

  const channelNameMap = new Map(channelStats.map(cs => [cs.channelId, cs.channelName]));

  // ── Per-channel conflict ratio ──────────────────────────────────────────────
  const byChannel = new Map<string, MessagingMessage[]>();
  for (const msg of messages) {
    if (!byChannel.has(msg.channelId)) byChannel.set(msg.channelId, []);
    byChannel.get(msg.channelId)!.push(msg);
  }

  for (const [channelId, chMsgs] of byChannel) {
    const flagged = chMsgs.filter(m => conflictScore(m.text) > 0);
    const ratio = flagged.length / chMsgs.length;
    if (ratio >= 0.15 && flagged.length >= 2) {
      const channelName = channelNameMap.get(channelId) ?? channelId;
      const involved = [...new Set(flagged.map(m => m.authorName))].slice(0, 3).join(', ');
      // Attach up to 5 flagged messages as context, sorted by time
      const relatedMessages: AnomalyRelatedMessage[] = [...flagged]
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
        .slice(0, 5)
        .map(m => ({ authorName: m.authorName, text: m.text, timestamp: m.timestamp }));
      anomalies.push({
        type: 'conflict_detected',
        severity: ratio >= 0.30 ? 'high' : 'medium',
        platform,
        channelId,
        channelName,
        description: `Conflict language in ${Math.round(ratio * 100)}% of #${channelName} messages today (${flagged.length} flagged). Involved: ${involved}.`,
        detectedAt: now,
        relatedMessages,
      });
    }
  }

  // ── Heated threads ──────────────────────────────────────────────────────────
  const heated = detectHeatedThreads(messages, channelNameMap);
  for (const ht of heated) {
    const vs = ht.authorNames.slice(0, 3).join(' vs ');
    anomalies.push({
      type: 'heated_thread',
      severity: ht.conflictMessageCount >= 4 ? 'high' : 'medium',
      platform,
      channelId: ht.channelId,
      channelName: ht.channelName,
      description: `Heated thread in #${ht.channelName}: ${ht.replyCount} rapid replies (avg ${ht.avgGapSeconds}s apart), ${ht.conflictMessageCount} conflict messages. ${vs}.`,
      detectedAt: now,
    });
  }

  // ── Negative reaction spikes ────────────────────────────────────────────────
  for (const msg of messages) {
    if (!msg.reactions || msg.reactions.length === 0) continue;

    const totalReactions = msg.reactions.reduce((s, r) => s + r.count, 0);
    const negativeReactions = msg.reactions
      .filter(r => isNegativeEmoji(r.emoji))
      .reduce((s, r) => s + r.count, 0);

    if (negativeReactions < 2) continue; // noise floor: need ≥2 negative reactions

    const negRatio = negativeReactions / totalReactions;
    if (negRatio < 0.4) continue; // need ≥40% negative before flagging

    const channelName = channelNameMap.get(msg.channelId) ?? msg.channelId;
    const negEmojis = msg.reactions
      .filter(r => isNegativeEmoji(r.emoji))
      .map(r => `${toEmoji(r.emoji.split('::')[0])} ×${r.count}`)
      .join('  ');

    // Include 1 message before and 1 after as context (sorted by timestamp)
    const chMsgs = byChannel.get(msg.channelId) ?? [];
    const sorted = [...chMsgs].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    const idx = sorted.findIndex(m => m.id === msg.id);
    const contextMsgs = sorted.slice(Math.max(0, idx - 1), idx + 2);
    const relatedMessages: AnomalyRelatedMessage[] = contextMsgs.map(m => ({
      authorName: m.authorName,
      text: m.text,
      timestamp: m.timestamp,
      reactions: m.reactions?.map(r => ({ emoji: r.emoji, count: r.count })),
    }));

    anomalies.push({
      type: 'negative_reaction_spike',
      severity: negativeReactions >= 5 || negRatio >= 0.7 ? 'high' : 'medium',
      platform,
      channelId: msg.channelId,
      channelName,
      memberId: msg.authorId,
      memberName: msg.authorName,
      messageId: msg.id,
      description: `${negativeReactions} negative reaction${negativeReactions > 1 ? 's' : ''} (${Math.round(negRatio * 100)}%) on a message by ${msg.authorName} in #${channelName}. Reactions: ${negEmojis}`,
      detectedAt: now,
      relatedMessages,
    });
  }

  return anomalies;
}
